import { AnimatePresence, motion } from 'framer-motion'
import { useState, useRef, useContext } from 'react'
import CardContext from '../../cardContext'
import type { PartialStory } from '../../momentTypes'
import { useWeekViewSettings } from '../../WeekViewSettingsContext'
import { CreateMomentCard } from './CreateMomentCard'
import { MomentCardBody } from './MomentCardBody'
import { MomentCardFooter } from './MomentCardFooter'
import type { Moment } from './momentCardTypes'
import { useMomentDragEvents } from './useMomentDragEvents'

import './styles.scss'

type NewCardProps = {
  onCreateMoment?: (
    momentName: string,
    selectedServices?: Array<{ id: string }>
  ) => void
  onRemoveCreator?: () => void
}

type FullCardProps = {
  activeFilters: string[]
  selectedFilters?: Record<string, unknown>
  className?: string
  closeRenamePopover?: () => void
  isContentLoading?: boolean
  isMomentDragActive?: boolean
  isRenamePopoverOpen?: boolean
  isSelectable?: boolean
  isSelected?: boolean
  moment?: Moment
  onClickMoment?: (navigateTo: (arg: any) => void, primaryBrand: string) => void
  onContextMenu?: () => void
  onDragRestrictedContent?: () => void
  story?: PartialStory
}

type MomentCardProps = FullCardProps & NewCardProps

export const DefaultMomentCard: React.FC<MomentCardProps> = ({
  activeFilters,
  selectedFilters,
  className,
  closeRenamePopover,
  isContentLoading,
  isMomentDragActive,
  isRenamePopoverOpen,
  isSelectable,
  isSelected,
  moment,
  onClickMoment,
  onContextMenu,
  onCreateMoment,
  onDragRestrictedContent,
  onRemoveCreator,
  story,
}) => {
  const momentCardRef = useRef(null)
  const { collapseContent } = useWeekViewSettings()
  const [hideCreator, setHideCreator] = useState(false)
  const { contextMenuCallbacks, onMoveContent, onReorderContent } =
    useContext(CardContext) ?? {}
  const { dragCount, onDragEnter, onDragLeave, onDropOnCard, onDragOver } =
    useMomentDragEvents({
      moment,
      isContentLoading: Boolean(isContentLoading),
      isMomentDragActive: Boolean(isMomentDragActive),
    })

  if (!moment && onCreateMoment && onRemoveCreator) {
    return (
      <AnimatePresence
        onExitComplete={() => {
          setHideCreator(false)
          onRemoveCreator()
        }}
      >
        {!hideCreator && (
          <motion.div
            key="create-moment-card-container"
            animate={{ scale: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            initial={{ scale: 0.5, opacity: 1 }}
            transition={{
              type: 'spring',
              bounce: 0.1,
              duration: 0.5,
            }}
          >
            <CreateMomentCard
              onCreateMoment={onCreateMoment}
              onExitForm={() => setHideCreator(true)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    )
  }

  return (
    <div
      onDrop={(e) => onDropOnCard({ e, onMoveContent, onReorderContent })}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
    >
      {moment && (
        <MomentCardBody
          className={className}
          closeRenamePopover={closeRenamePopover}
          contextMenuCallbacks={contextMenuCallbacks}
          isRenamePopoverOpen={isRenamePopoverOpen}
          isSelectable={isSelectable}
          isSelected={isSelected}
          moment={moment}
          momentCardRef={momentCardRef}
          onClickMoment={onClickMoment}
          onContextMenu={onContextMenu}
        />
      )}
      <MomentCardFooter
        activeFilters={activeFilters}
        selectedFilters={selectedFilters}
        collapseContent={collapseContent}
        contextMenuCallbacks={contextMenuCallbacks}
        isContentDropzoneActive={Boolean(dragCount)}
        isMomentDragActive={isMomentDragActive}
        isSelected={isSelected}
        moment={moment}
        momentCardRef={momentCardRef}
        onDragRestrictedContent={onDragRestrictedContent}
        services={moment?.posts?.map((post) => post.service)}
        story={story}
      />
    </div>
  )
}
